import type { PolicyFieldsFragment, PolicyUserRoleFieldsFragment } from '@salescore/client-api'
import { type PolicyAction, policyActionSchema } from '@salescore/features'
import { Record } from 'effect'
import { isNullish } from 'effect/Predicate'
import { z } from 'zod'

export const PolicyFormSchema = z.object({
  label: z.string(),
  description: z.string().optional(),
  actionsWithPermission: z.object({
    [policyActionSchema.enum['sheet-create']]: z.boolean(),
    [policyActionSchema.enum['sheet-update']]: z.boolean(),
    [policyActionSchema.enum['sheet-delete']]: z.boolean(),
    [policyActionSchema.enum['sheet-create-new-record']]: z.boolean(),
    [policyActionSchema.enum['sheet-save-record']]: z.boolean(),
    [policyActionSchema.enum['sheet-delete-record']]: z.boolean(),
    [policyActionSchema.enum['sheet-open-relation-input-form']]: z.boolean(),
    [policyActionSchema.enum['sheet-download-csv']]: z.boolean(),
    [policyActionSchema.enum['convert-lead']]: z.boolean(),
    [policyActionSchema.enum['kpi-create']]: z.boolean(),
    [policyActionSchema.enum['kpi-update']]: z.boolean(),
    [policyActionSchema.enum['kpi-delete']]: z.boolean(),
    [policyActionSchema.enum['kpi-create-new-record']]: z.boolean(),
    [policyActionSchema.enum['kpi-save-record']]: z.boolean(),
    [policyActionSchema.enum['kpi-delete-record']]: z.boolean(),
    [policyActionSchema.enum['kpi-open-relation-input-form']]: z.boolean(),
    [policyActionSchema.enum['kpi-download-csv']]: z.boolean(),
    [policyActionSchema.enum['kpi-pivot-create']]: z.boolean(),
    [policyActionSchema.enum['kpi-pivot-update']]: z.boolean(),
    [policyActionSchema.enum['kpi-pivot-delete']]: z.boolean(),
    [policyActionSchema.enum['kpi-pivot-download-csv']]: z.boolean(),
    [policyActionSchema.enum['view-group-create']]: z.boolean(),
    [policyActionSchema.enum['view-group-delete']]: z.boolean(),
    [policyActionSchema.enum['view-display-config-edit']]: z.boolean(),
    [policyActionSchema.enum['manage-organization-setting']]: z.boolean(),
    [policyActionSchema.enum['manage-connection']]: z.boolean(),
    [policyActionSchema.enum['manage-elt-model']]: z.boolean(),
    [policyActionSchema.enum['manage-goal']]: z.boolean(),
    [policyActionSchema.enum['manage-dimension-group']]: z.boolean(),
    [policyActionSchema.enum['manage-users']]: z.boolean(),
    [policyActionSchema.enum['manage-user-groups']]: z.boolean(),
    [policyActionSchema.enum['manage-invitation']]: z.boolean(),
    [policyActionSchema.enum['read-user-activity']]: z.boolean(),
  }),
})
export type PolicyFormValue = z.infer<typeof PolicyFormSchema>

export const buildActionsWithPermission = (
  actionsWithPermission: PolicyFormValue['actionsWithPermission'],
): Array<{ action: PolicyAction; permission: boolean }> =>
  Record.collect(actionsWithPermission, (action, permission) => ({ action, permission }))

export const buildUniformActionsWithPermission = (permission: boolean): PolicyFormValue['actionsWithPermission'] =>
  Record.map(PolicyFormSchema.shape.actionsWithPermission.shape, () => permission)

export const buildDefaultPolicyFormValue = (): PolicyFormValue => ({
  label: '',
  description: undefined,
  actionsWithPermission: buildUniformActionsWithPermission(true),
})

export const buildPolicyFormValue = (
  policyUserRole: PolicyUserRoleFieldsFragment,
  policies: PolicyFieldsFragment[],
): PolicyFormValue => {
  const rolePolicies = policies.filter(
    (policy) =>
      policy.principal.type === 'userRole' &&
      policy.principal.role === policyUserRole.name &&
      // fetchPolicies は subjectId を渡していないため view / viewGroup 紐づきの行も返る
      // (apps/backend/web/src/graphql/queries/PoliciesQuery.ts)。このフォームが編集するのは
      // subject なしのロール権限なので、除外しないと同一 action で複数行が候補になる。
      isNullish(policy.subject),
  )

  return {
    label: policyUserRole.label,
    description: policyUserRole.description,
    // 行が無い action は false で埋める。undefined のままでは Switch が uncontrolled になり、
    // 触らずに保存すると PolicyFormSchema の検証で弾かれる。false を選ぶ根拠は、ロールに一致する
    // policy が無いときの判定 fallback が false で、カスタムロール名に効くデフォルトポリシーが
    // 定義されていないこと (policy/ability.ts の decideCan、policy/defaultPolicies.ts)。
    // ここで組むのはロール自身の明示設定であって実効権限ではない (同一 action に everyone policy が
    // 別に存在すれば、この値が false でも実効権限は true になりうる)。
    //
    // 同一 action の行が 1 つである前提で先頭を採る。unique index は
    // (organizationId, principal, action, viewId, viewGroupId) にあるが、viewId / viewGroupId が
    // NULL の行は PostgreSQL の NULLS DISTINCT で重複を許すため DB では保証されない。保存が
    // deleteMany → createMany の全置換なので正常系では 1 行で、重複するのは並行保存が競合した場合
    // だけ。そのときどの行を採っても「いずれかの保存結果」になるので、順序は問わない。
    actionsWithPermission: Record.map(
      PolicyFormSchema.shape.actionsWithPermission.shape,
      (_, action) => rolePolicies.find((policy) => policy.action === action)?.permission ?? false,
    ),
  }
}
